import React, { useEffect, useState } from "react";
import {
  AppBar,
  Button,
  Box,
  Stack,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";

const isElementsOverlapping = (element1, element2) => {
  const rect1 = element1.getBoundingClientRect();
  const rect2 = element2.getBoundingClientRect();

  return !(
    rect1.bottom < rect2.top ||
    rect1.top > rect2.bottom ||
    rect1.right < rect2.left ||
    rect1.left > rect2.right
  );
};

const Header = () => {
  const [isOverlapping, setIsOverlapping] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector("header");
      const sections = [
        "info",
        "case",
        "faq",
        "PrivacyPolicy",
        "member",
        "about",
        "contact",
        "service",
      ]
        .map((id) => document.getElementById(id))
        .filter(Boolean);

      if (header) {
        const isOverlapping = sections.some((el) =>
          isElementsOverlapping(header, el),
        );
        setIsOverlapping(isOverlapping);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleNavClick = (id) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(id), 100);
    } else {
      scrollToSection(id);
    }
    setDrawerOpen(false);
  };

  const navLinks = [
    { label: "私たちについて", sub: "About us", id: "about" },
    { label: "サービス紹介", sub: "Service", id: "service" },
    { label: "メンバー紹介", sub: "Member", id: "member" },
    { label: "事例紹介", sub: "Cases", id: "case" },
    { label: "よくある質問", sub: "FAQ", id: "faq" },
    { label: "会社概要", sub: "Company", id: "company" },
    { label: "お問い合わせ", sub: "Contact", id: "contact" },
  ];

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "transparent",
          boxShadow: "none",
          pt: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* ロゴ */}
          <Box
            component="img"
            src={
              isOverlapping ? "header/logo_color.svg" : "header/logo_white.svg"
            }
            sx={{
              height: 40,
              ml: 2,
            }}
          />

          {/* スマホ用：ハンバーガーメニュー */}
          {isMobile ? (
            <IconButton
              onClick={toggleDrawer}
              sx={{
                background: isOverlapping ? "#E40614" : "#fff",
                color: isOverlapping ? "#fff" : "#E40614",
                ml: "auto",
                width: 40,
                height: 40,
              }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box
              sx={{
                ml: "auto",
                backgroundColor: isOverlapping ? "#E40614" : "#fff",
                borderRadius: "999px",
                px: 3,
                py: 1,
                display: "inline-flex",
                alignItems: "center",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Stack direction="row" spacing={3}>
                {navLinks.slice(0, 6).map((nav) => (
                  <Button
                    key={nav.label}
                    onClick={() => handleNavClick(nav.id)}
                    sx={{
                      color: isOverlapping ? "#fff" : "#000",
                      fontWeight: "bold",
                      fontFamily: "Roboto, sans-serif",
                      paddingBottom: "3px",
                      backgroundImage: isOverlapping
                        ? "linear-gradient(#fff, #fff)"
                        : "linear-gradient(#000, #000)",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "bottom right",
                      backgroundSize: "0 1px",
                      transition: "background-size 0.3s",
                      "&:hover": {
                        backgroundColor: "transparent",
                        backgroundPosition: "bottom left",
                        backgroundSize: "100% 1px",
                      },
                    }}
                  >
                    {nav.label}
                  </Button>
                ))}

                {/* お問い合わせボタン (デスクトップ) */}
                <Button
                  variant="contained"
                  onClick={() => handleNavClick("contact")}
                  sx={{
                    backgroundColor: isOverlapping ? "#fff" : "red",
                    color: isOverlapping ? "#000" : "#fff",
                    borderRadius: "50px",
                    px: 4,
                    fontWeight: "bold",
                    fontFamily: "Roboto, sans-serif",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                    transition:
                      "transform 0.2s ease-in-out, background-color 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: isOverlapping ? "#f0f0f0" : "#c70000",
                      transform: "scale(1.1, 1.1)",
                    },
                  }}
                >
                  お問い合わせ
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      </AppBar>

      {/* スマホ用：ドロワー */}
      <Drawer anchor="top" open={drawerOpen} onClose={toggleDrawer}>
        <Box
          sx={{
            height: "100%",
            backgroundColor: "#fff",
            p: 3,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            paddingBottom: 20,
          }}
        >
          {/* ロゴ + 閉じるボタンを同じ高さで横並び */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
              padding: "15px",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box
              component="img"
              src="header/logo_color.svg"
              alt="logo"
              sx={{ height: 39 }}
            />
            <IconButton
              onClick={toggleDrawer}
              sx={{
                backgroundColor: "#E40614",
                color: "#fff",
                width: 40,
                height: 40,
                borderRadius: "50%",
                "&:hover": {
                  backgroundColor: "#c70000",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          {/* メニューリスト */}
          <List sx={{ flexGrow: 1 }}>
            {navLinks.slice(0, 6).map(({ label, sub, id }) => (
              <ListItem key={label} disablePadding sx={{ mb: 2 }}>
                <ListItemButton onClick={() => handleNavClick(id)}>
                  <ListItemText
                    primary={
                      <>
                        <Box
                          component="span"
                          sx={{
                            fontSize: 14,
                            fontFamily: "Roboto, sans-serif",
                            color: "#666",
                            display: "block",
                          }}
                        >
                          {sub}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            fontWeight: "bold",
                            fontFamily: "Roboto, sans-serif",
                            color: "#E40614",
                            fontSize: 24,
                          }}
                        >
                          {label}
                        </Box>
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {/* お問い合わせボタン (ドロワー) */}
          <Button
            variant="contained"
            onClick={() => handleNavClick("contact")}
            sx={{
              backgroundColor: "#E40614",
              borderRadius: "50px",
              color: "#fff",
              fontWeight: "bold",
              fontSize: 14,
              py: 1.5,
              width: "100%",
              "&:hover": {
                backgroundColor: "#c70000",
              },
            }}
          >
            お問い合わせ
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default Header;
